// Client API admin per encounters: lista + dettaglio (Sprint 3).

import { z } from 'zod';
import { request } from './_request';

const EncounterListItemSchema = z.object({
  id: z.string().uuid(),
  pseudonym: z.string(),
  status: z.enum(['in_progress', 'completed', 'abandoned', 'aborted_emergency']),
  language_detected: z.string().nullable(),
  language_script: z.string().nullable(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  total_turns: z.number().int(),
  completeness_pct: z.number().int(),
  emergency_triggered: z.boolean(),
  pickup_code: z.string(),
  flags_count: z.number().int(),
  outcomes_count: z.number().int(),
});

const EncountersListResponseSchema = z.object({
  ok: z.literal(true),
  total: z.number().int(),
  page: z.number().int(),
  per_page: z.number().int(),
  encounters: z.array(EncounterListItemSchema),
});

export type EncounterListItem = z.infer<typeof EncounterListItemSchema>;

export async function listEncounters(params?: {
  page?: number;
  per_page?: number;
  status?: 'in_progress' | 'completed' | 'abandoned' | 'aborted_emergency';
  date_from?: string; // ISO 8601
  date_to?: string;
}): Promise<{
  total: number;
  page: number;
  per_page: number;
  encounters: EncounterListItem[];
}> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.per_page) search.set('per_page', String(params.per_page));
  if (params?.status) search.set('status', params.status);
  if (params?.date_from) search.set('date_from', params.date_from);
  if (params?.date_to) search.set('date_to', params.date_to);
  const qs = search.toString();
  const json = await request<unknown>(`/api/admin/encounters${qs ? `?${qs}` : ''}`);
  return EncountersListResponseSchema.parse(json);
}

const EncounterDetailMessageSchema = z.object({
  turn: z.number().int(),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string().nullable(),
  content_translation_it: z.string().nullable(),
  timestamp: z.string(),
  llm_model_used: z.string().nullable(),
  llm_tokens: z
    .object({
      input: z.number().int().nullable(),
      output: z.number().int().nullable(),
      cached: z.number().int().nullable(),
    })
    .nullable(),
});

const CapturedDataItemSchema = z.object({
  id: z.string().uuid(),
  question_id: z.string().uuid(),
  question_objective: z.string(),
  question_tags: z.array(z.string()),
  captured_value: z.unknown(),
  captured_value_translation_it: z.object({ text: z.string() }).nullable().optional(),
  confidence: z.enum(['high', 'medium', 'low']),
  captured_at: z.string(),
});

const FlagEventItemSchema = z.object({
  id: z.string().uuid(),
  flag_name: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  trigger_keyword: z.string().nullable(),
  question_id: z.string().uuid().nullable(),
  triggered_at: z.string(),
});

const OutcomeResultItemSchema = z.object({
  id: z.string().uuid(),
  outcome_id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  action: z.unknown(),
  priority: z.number().int(),
  presented_to_user: z.boolean(),
  user_acknowledged: z.boolean(),
});

const EncounterDetailResponseSchema = z.object({
  ok: z.literal(true),
  encounter: z.object({
    id: z.string().uuid(),
    pseudonym: z.string(),
    status: z.enum(['in_progress', 'completed', 'abandoned', 'aborted_emergency']),
    language_detected: z.string().nullable(),
    language_script: z.string().nullable(),
    started_at: z.string(),
    completed_at: z.string().nullable(),
    duration_seconds: z.number().int().nullable(),
    total_turns: z.number().int(),
    completeness_pct: z.number().int(),
    emergency_triggered: z.boolean(),
    emergency_keyword: z.string().nullable(),
    pickup_code: z.string(),
    reviewed_at: z.string().nullable(),
    reviewed_by_admin_id: z.string().nullable(),
    marked_clinical_relevant: z.boolean(),
    operator_notes: z.string().nullable(),
    flow: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
        version_number: z.number().int(),
      })
      .nullable(),
    token_stats: z.object({
      input: z.number().int(),
      output: z.number().int(),
      cached: z.number().int(),
    }),
  }),
  messages: z.array(EncounterDetailMessageSchema),
  captured_data: z.array(CapturedDataItemSchema),
  flag_events: z.array(FlagEventItemSchema),
  outcome_results: z.array(OutcomeResultItemSchema),
});

export type EncounterDetail = z.infer<typeof EncounterDetailResponseSchema>;

export async function getEncounter(id: string): Promise<EncounterDetail> {
  const json = await request<unknown>(`/api/admin/encounters/${encodeURIComponent(id)}`);
  return EncounterDetailResponseSchema.parse(json);
}

export async function markEncounterReviewed(id: string): Promise<{ ok: true }> {
  const json = await request<unknown>(
    `/api/admin/encounters/${encodeURIComponent(id)}/mark-reviewed`,
    {
      method: 'POST',
    },
  );
  return z.object({ ok: z.literal(true) }).parse(json);
}

export async function markEncounterClinical(
  id: string,
  value: boolean,
): Promise<{ ok: true; marked_clinical_relevant: boolean }> {
  const json = await request<unknown>(
    `/api/admin/encounters/${encodeURIComponent(id)}/mark-clinical`,
    {
      method: 'POST',
      body: JSON.stringify({ value }),
    },
  );
  return z.object({ ok: z.literal(true), marked_clinical_relevant: z.boolean() }).parse(json);
}

export async function updateEncounterNotes(id: string, notes: string): Promise<{ ok: true }> {
  const json = await request<unknown>(`/api/admin/encounters/${encodeURIComponent(id)}/notes`, {
    method: 'PATCH',
    body: JSON.stringify({ notes }),
  });
  return z.object({ ok: z.literal(true) }).parse(json);
}

export type EncounterPdfMode = 'it' | 'bilingual';

export function downloadEncounterPdfUrl(id: string, mode: EncounterPdfMode = 'it'): string {
  const base = `/api/admin/encounters/${encodeURIComponent(id)}/pdf`;
  return mode === 'it' ? base : `${base}?mode=${mode}`;
}
